import type { ThemeConfig } from "../types";

/**
 * Wondermaps Default
 *
 * Copied from org "san-francisco-zoo" effective theme.
 */
export const wondermapsDefault: ThemeConfig = {
  colors: {
    primary: "#f59e0b",
    maskColor: "#ffffff",
    background: "#f5f5f4"
  },
  layers: {
    bus: {
      stroke: "#78716c",
      zOrder: 3,
      strokeWidth: 2
    },
    prop: {
      fill: "#bbf7d0",
      stroke: "#16a34a",
      zOrder: 7.5,
      fillOpacity: 1,
      strokeWidth: 1
    },
    rail: {
      stroke: "#939185",
      zOrder: 2.75,
      strokeWidth: 2,
      strokeDasharray: [
        6,
        2
      ]
    },
    zone: {
      fill: "#e0f2fe",
      stroke: "#0ea5e9",
      zOrder: 1.5,
      fillOpacity: 0.5,
      strokeWidth: 1
    },
    water: {
      fill: "#62959C",
      smooth: true,
      stroke: "#62959C",
      zOrder: 0.5,
      fillOpacity: 1,
      strokeWidth: 0,
      smoothIterations: 2
    },
    bridge: {
      stroke: "#C7B7A3",
      zOrder: 8.5,
      strokeWidth: 3,
      smooth: true,
      lineCap: "butt",
      strokeDasharray: [
        4,
        2
      ],
      strokeWidthZoom: {
        maxZoom: 18,
        minZoom: 15,
        maxWidth: 29,
        minWidth: 4
      }
    },
    tunnel: {
      stroke: "#D0C9C0",
      zOrder: 4.25,
      strokeWidth: 2,
      strokeDasharray: [
        2,
        2
      ],
      smooth: false,
      strokeWidthZoom: {
        maxZoom: 18,
        minZoom: 15,
        maxWidth: 20,
        minWidth: 2
      }
    },
    barrier: {
      stroke: "#674331",
      zOrder: 2.5,
      strokeWidth: 1,
      strokeDasharray: [
        4,
        4
      ],
      smooth: true,
      smoothIterations: 2
    },
    parking: {
      fill: "#D1D1D1",
      stroke: "#D1D1D1",
      zOrder: 1.75,
      fillOpacity: 1,
      strokeWidth: 1
    },
    boundary: {
      fill: "#ADC2A9",
      stroke: "#ADC2A9",
      zOrder: 0.25,
      fillOpacity: 1,
      strokeWidth: 2
    },
    building: {
      fill: "#A27B5C",
      stroke: "#A27B5C",
      zOrder: 4,
      depthEffect: {
        enabled: true,
        shadowColor: "#674331",
        shadowOffset: 1.5,
        shadowOpacity: 0.45
      },
      fillOpacity: 1,
      strokeWidth: 0,
      smooth: true,
      cornerRadius: 0.75,
      smoothIterations: 1
    },
    vignette: {
      fill: "#ffffff",
      zOrder: 9,
      fillOpacity: 0.8
    },
    waterway: {
      stroke: "#62959C",
      zOrder: 0.75,
      strokeWidth: 2,
      strokeWidthZoom: {
        maxZoom: 18,
        minZoom: 12,
        maxWidth: 24,
        minWidth: 3.5
      },
      smooth: true
    },
    landscape: {
      fill: "#91AC8F",
      smooth: true,
      stroke: "#91AC8F",
      zOrder: 1.25,
      fillOpacity: 0.5,
      strokeWidth: 1,
      smoothIterations: 1
    },
    vegetation: {
      fill: "#91AC8F",
      smooth: true,
      stroke: "#91AC8F",
      zOrder: 1,
      fillOpacity: 1,
      strokeWidth: 0,
      smoothIterations: 2
    },
    custom_path: {
      casing: {
        color: "#7e22ce",
        width: 2,
        enabled: false
      },
      smooth: true,
      stroke: "#a855f7",
      zOrder: 5.5,
      strokeWidth: 3,
      strokeDasharray: [
        8,
        6
      ],
      strokeWidthZoom: {
        maxZoom: 18,
        minZoom: 12,
        maxWidth: 12,
        minWidth: 2
      }
    },
    path_primary: {
      smooth: true,
      stroke: "#E6E6E6",
      zOrder: 2.25,
      strokeWidth: 4,
      strokeWidthZoom: {
        maxZoom: 18,
        minZoom: 15,
        maxWidth: 20,
        minWidth: 2
      },
      lineCap: "butt",
      smoothIterations: 2
    },
    path_wide: {
      smooth: true,
      stroke: "#ECECEC",
      zOrder: 3.8,
      strokeWidth: 10,
      strokeWidthZoom: {
        maxZoom: 18,
        minZoom: 15,
        maxWidth: 45,
        minWidth: 4
      },
      lineCap: "butt",
      smoothIterations: 2
    },
    // Theme-only key. `path_wide` Polygon features (plazas) render through
    // this entry instead of `path_wide` so admins can tune the plaza look
    // independently of wide-path line styling.
    plaza: {
      fill: "#ECECEC",
      fillOpacity: 1,
      stroke: "#DDDDDD",
      strokeWidth: 1,
      zOrder: 3.7,
      // Smoothing OFF by default. Chaikin moves perimeter vertices, breaking
      // `coordToId` connectivity with snapped sibling-line endpoints — the
      // plaza becomes orphaned in the published routing graph even when the
      // raw draft is fully connected. Admins can opt in via the theme editor.
      smooth: false,
      smoothIterations: 1
    },
    custom_polygon: {
      fill: "#f3e8ff",
      stroke: "#a855f7",
      zOrder: 7,
      fillOpacity: 0.5,
      strokeWidth: 1
    },
    path_secondary: {
      smooth: true,
      stroke: "#E6E6E6",
      zOrder: 2,
      strokeWidth: 2,
      strokeWidthZoom: {
        maxZoom: 18,
        minZoom: 15,
        maxWidth: 20,
        minWidth: 2
      }
    },
    path_tertiary: {
      smooth: true,
      stroke: "#D4D4D4",
      zOrder: 1.75,
      strokeWidth: 1.5,
      strokeDasharray: [6, 4],
      strokeWidthZoom: {
        maxZoom: 18,
        minZoom: 15,
        maxWidth: 12,
        minWidth: 1.5
      },
      smoothIterations: 2
    },
    building_primary: {
      fill: "#A27B5C",
      stroke: "#A27B5C",
      zOrder: 3.75,
      depthEffect: {
        enabled: true,
        shadowColor: "#674331",
        shadowOffset: 2,
        shadowOpacity: 0.3
      },
      fillOpacity: 1,
      strokeWidth: 2,
      smooth: true,
      cornerRadius: 0.75,
      smoothIterations: 1
    },
    building_entrance: {
      fill: "#A27B5C",
      stroke: "#A27B5C",
      zOrder: 3.5,
      depthEffect: {
        enabled: true,
        shadowColor: "#674331",
        shadowOffset: 2,
        shadowOpacity: 0.3
      },
      fillOpacity: 1,
      strokeWidth: 2,
      smooth: true,
      cornerRadius: 0.75,
      smoothIterations: 1
    },
    building_secondary: {
      fill: "#A27B5C",
      stroke: "#A27B5C",
      zOrder: 3.25,
      depthEffect: {
        enabled: true,
        shadowColor: "#674331",
        shadowOffset: 2,
        shadowOpacity: 0.3
      },
      fillOpacity: 0.9,
      strokeWidth: 1,
      smooth: true,
      cornerRadius: 0.75,
      smoothIterations: 1
    }
  },
  poi: {
    iconSize: 14,
    iconColor: "#ffffff",
    shadowBlur: 3,
    circleColor: "#4f46e5",
    shadowColor: "rgba(0,0,0,0.25)",
    circleRadius: 16,
    shadowOffsetY: 2,
    circleStrokeColor: "#ffffff",
    circleStrokeWidth: 2
  },
  poiCategories: {
    atm: {
      icon: "dollar-sign",
      tier: 2,
      visible: true,
      circleColor: "#22C55E",
      displayName: "ATM"
    },
    food: {
      icon: "utensils",
      tier: 2,
      visible: true,
      circleColor: "#F59E0B",
      displayName: "Food"
    },
    game: {
      icon: "gamepad-2",
      tier: 3,
      visible: true,
      circleColor: "#F59E0B",
      displayName: "Game"
    },
    ride: {
      icon: "ferris-wheel",
      tier: 1,
      visible: true,
      circleColor: "#EF4444",
      displayName: "Ride"
    },
    shop: {
      icon: "bag-shopping",
      tier: 2,
      visible: true,
      circleColor: "#8B5CF6",
      displayName: "Shop"
    },
    show: {
      icon: "ticket",
      tier: 2,
      visible: true,
      circleColor: "#7C3AED",
      displayName: "Show"
    },
    event: {
      icon: "calendar",
      tier: 3,
      visible: true,
      circleColor: "#A855F7",
      displayName: "Event"
    },
    stage: {
      icon: "microphone",
      tier: 2,
      visible: true,
      circleColor: "#EC4899",
      displayName: "Stage"
    },
    animal: {
      icon: "hippo",
      tier: 1,
      visible: true,
      circleColor: "#b9afa0",
      displayName: "Animal"
    },
    garden: {
      icon: "flower-2",
      tier: 1,
      visible: true,
      circleColor: "#22C55E",
      displayName: "Garden"
    },
    picnic: {
      icon: "tree",
      tier: 3,
      visible: true,
      circleColor: "#84CC16",
      displayName: "Picnic"
    },
    sports: {
      icon: "dumbbell",
      tier: 3,
      visible: true,
      circleColor: "#22C55E",
      displayName: "Sports"
    },
    vendor: {
      icon: "store",
      tier: 3,
      visible: true,
      circleColor: "#8B5CF6",
      displayName: "Vendor"
    },
    artwork: {
      icon: "palette",
      tier: 3,
      visible: true,
      circleColor: "#8B5CF6",
      displayName: "Artwork"
    },
    exhibit: {
      icon: "landmark",
      tier: 1,
      visible: true,
      circleColor: "#0891B2",
      displayName: "Exhibit"
    },
    parking: {
      icon: "car",
      tier: 2,
      visible: true,
      circleColor: "#64748B",
      displayName: "Parking"
    },
    service: {
      icon: "bell-concierge",
      tier: 3,
      visible: true,
      circleColor: "#6B7280",
      displayName: "Service"
    },
    utility: {
      icon: "circle-info",
      tier: 3,
      visible: true,
      circleColor: "#6B7280",
      displayName: "Utility"
    },
    entrance: {
      icon: "door-open",
      tier: 1,
      visible: true,
      circleColor: "#10B981",
      displayName: "Entrance"
    },
    fountain: {
      icon: "droplets",
      tier: 3,
      visible: true,
      circleColor: "#0EA5E9",
      displayName: "Fountain"
    },
    restroom: {
      icon: "restroom",
      tier: 2,
      visible: true,
      circleColor: "#6B7280",
      displayName: "Restroom"
    },
    emergency: {
      icon: "kit-medical",
      tier: 3,
      visible: true,
      circleColor: "#EF4444",
      displayName: "Emergency"
    },
    first_aid: {
      icon: "kit-medical",
      tier: 2,
      visible: true,
      circleColor: "#EF4444",
      displayName: "First Aid"
    },
    viewpoint: {
      icon: "eye",
      tier: 3,
      visible: true,
      circleColor: "#F59E0B",
      displayName: "Viewpoint"
    },
    attraction: {
      icon: "star",
      tier: 1,
      visible: true,
      circleColor: "#3B82F6",
      displayName: "Attraction"
    },
    greenhouse: {
      icon: "home",
      tier: 3,
      visible: true,
      circleColor: "#84CC16",
      displayName: "Greenhouse"
    },
    playground: {
      icon: "baby",
      tier: 3,
      visible: true,
      circleColor: "#EC4899",
      displayName: "Playground"
    },
    information: {
      icon: "circle-info",
      tier: 2,
      visible: true,
      circleColor: "#3B82F6",
      displayName: "Information"
    }
  },
  poiMinZoom: {
    "1": 16,
    "2": 17,
    "3": 18
  },
  vignette: {
    opacity: 0.8,
    gradientCurve: "ease-out",
    gradientSteps: 5,
    gradientWidth: 50
  },
  props: {
    bush: {
      icon: "prop-bush",
      zOrder: 8,
      minZoom: 14,
      iconSize: 0.45,
      iconOpacity: 1
    },
    lamp: {
      icon: "prop-lamp",
      zOrder: 7.75,
      minZoom: 15,
      iconSize: 0.35,
      iconOpacity: 1
    },
    rock: {
      icon: "prop-rock",
      zOrder: 7.5,
      minZoom: 14,
      iconSize: 0.4,
      iconOpacity: 1
    },
    tree: {
      icon: "prop-tree",
      zOrder: 7.25,
      minZoom: 14,
      iconSize: 0.55,
      iconOpacity: 1
    },
    "tree-palm": {
      icon: "prop-palm",
      zOrder: 6.75,
      minZoom: 14,
      iconSize: 0.55,
      iconOpacity: 1
    },
    "tree-pine": {
      icon: "prop-pine",
      zOrder: 6.5,
      minZoom: 14,
      iconSize: 0.55,
      iconOpacity: 1
    },
    bench: {
      icon: "prop-bench",
      iconSize: 0.06,
      iconOpacity: 1,
      zOrder: 7
    },
    "picnic-table": {
      icon: "prop-picnic-table",
      iconSize: 0.06,
      iconOpacity: 1,
      zOrder: 6.25
    },
    "waste-basket": {
      icon: "prop-waste-basket",
      iconSize: 0.06,
      iconOpacity: 1,
      zOrder: 6
    },
    "drinking-water": {
      icon: "prop-drinking-water",
      iconSize: 0.06,
      iconOpacity: 1,
      zOrder: 5.75
    },
    "bicycle-parking": {
      icon: "prop-bicycle-parking",
      iconSize: 0.06,
      iconOpacity: 1,
      zOrder: 5.5
    },
    "charging-station": {
      icon: "prop-charging-station",
      iconSize: 0.06,
      iconOpacity: 1,
      zOrder: 5.25
    },
    "motorcycle-parking": {
      icon: "prop-motorcycle-parking",
      iconSize: 0.06,
      iconOpacity: 1,
      zOrder: 5
    }
  },
  shadowDirection: {
    x: 1,
    y: 1
  },
  sponsorRings: {
    gold: {
      color: "#ded500",
      glowBlur: 2,
      glowOpacity: 1,
      radiusRatio: 1,
      strokeWidth: 6
    },
    silver: {
      color: "#A8A9AD",
      glowBlur: 1.5,
      glowOpacity: 0.4,
      radiusRatio: 1.2,
      strokeWidth: 2.5
    },
    bronze: {
      color: "#CD7F32",
      glowBlur: 1,
      glowOpacity: 0.3,
      radiusRatio: 1.2,
      strokeWidth: 2
    }
  },
  shadowLayers: 1
};
